import {FormLabel} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import {
    InjuryActivityStatus,
    InjuryActivityType,
    InjuryDrugAlcohol,
    InjuryIntent,
    InjuryMechanism,
    PlaceOfInjury,
} from '../../../../controls/agyle/ecds';
import moment from 'moment';
import Validator from '../../../../util/validator';
import config from '../../../../app/config';
import _ from 'lodash';
import {UtilsMomentJS} from '../../../../store/utilsMoment';
import {useStylesECDS} from './ecdsStyle';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {cleanObject} from '../../../../util/patientContextUtil';

const Injury = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const firstUpdate = useRef(true);

    // Injury Dataset
    const [injury, setInjury] = useState({
        InjuryDateTime: null,
        EmergencyCarePlaceOfInjury_SnomedCt: null,
        Latitude: '',
        Longitude: '',
        EmergencyCareInjuryIntent_SnomedCt: null,
        EmergencyCareInjuryActivityStatus_SnomedCt: null,
        EmergencyCareInjuryActivityType_SnomedCt: null,
        EmergencyCareInjuryMechanism_SnomedCt: null,
        EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt: [],
        AssaultLocationDescription: '',
    });

    // Validation
    const [error, setError] = useState({
        dateTime: !props.ecds,
        place: false,
        latitude: false,
        longitude: false,
        intent: false,
        status: false,
        type: false,
        mechanism: false,
        alcoholAndDrug: false,
        assault: false,
    });

    // For ReadOnly Data
    const disableData = useRef({});

    // invalid pickers
    const errorPicker = useRef(null);
    const [toggleCord, setToggleCord] = useState(false);
    const [drugsAlcoholInvolved, setDrugsAlcoholInvolved] = useState(props.drugsAlcoholInvolvedValue || false);

    useEffect(() => {
        if (!_.isNil(props.selectedForm)) {
            // Crate local date from UTC date
            const dateUTC = UtilsMomentJS.getLocalDateFromUTCECDSFormat(props.selectedForm?.injuryDate, props.selectedForm?.injuryTime);

            setInjury(prevState => ({
                ...prevState,
                InjuryDateTime: props.selectedForm?.injuryDateTime ? moment(props.selectedForm?.injuryDateTime) : dateUTC,
                EmergencyCarePlaceOfInjury_SnomedCt: props.selectedForm?.placeOfInjury ? props.selectedForm?.placeOfInjury : null,
                EmergencyCareInjuryIntent_SnomedCt: props.selectedForm?.injuryIntent ? props.selectedForm?.injuryIntent : null,
                EmergencyCareInjuryActivityStatus_SnomedCt: props.selectedForm?.injuryActivityStatus ? props.selectedForm?.injuryActivityStatus : null,
                EmergencyCareInjuryActivityType_SnomedCt: props.selectedForm?.injuryActivityType ? props.selectedForm?.injuryActivityType : null,
                EmergencyCareInjuryMechanism_SnomedCt: props.selectedForm?.injuryMechanism ? props.selectedForm?.injuryMechanism : null,
                EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt: props.selectedForm?.injuryAlcoholOrDrug ? props.selectedForm?.injuryAlcoholOrDrug : [],
                AssaultLocationDescription: props.selectedForm?.assaultLocationDescription || '',
            }));
        } else if (props.ecds) {
            let data = {};
            let dataToBlock = {};
            if (!_.isEmpty(props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.InjuryCharacteristics)) {
                const injury = props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.InjuryCharacteristics;
                const dateUTC = UtilsMomentJS.getLocalDateFromUTCECDSFormat(injury?.InjuryDate, injury?.InjuryTime);

                dataToBlock = {
                    ...dataToBlock,
                    InjuryDateTime: injury?.InjuryDateTime ? moment(injury?.InjuryDateTime) : dateUTC,
                    Latitude: injury?.EmergencyCarePlaceOfInjury_LatitudeAndLongitude?.Latitude || '',
                    Longitude: injury?.EmergencyCarePlaceOfInjury_LatitudeAndLongitude?.Longitude || '',
                };
            }

            if (!_.isEmpty(props.patient?.studies?.injury?.list)) {
                const lastInjury = props.patient.studies.injury.list[0];
                const dateUTC = UtilsMomentJS.getLocalDateFromUTCECDSFormat(lastInjury?.injuryDate, lastInjury?.injuryTime);
                dataToBlock = {
                    ...dataToBlock,
                    InjuryDateTime: lastInjury?.injuryDateTime ? moment(lastInjury?.injuryDateTime) : dateUTC,
                    EmergencyCarePlaceOfInjury_SnomedCt: lastInjury.placeOfInjury || null,
                    EmergencyCareInjuryIntent_SnomedCt: lastInjury.injuryIntent || null,
                    EmergencyCareInjuryActivityStatus_SnomedCt: lastInjury.injuryActivityStatus || null,
                    EmergencyCareInjuryActivityType_SnomedCt: lastInjury.injuryActivityType || null,
                    EmergencyCareInjuryMechanism_SnomedCt: lastInjury.injuryMechanism || null,
                    EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt: lastInjury.injuryAlcoholOrDrug || [],
                    AssaultLocationDescription: lastInjury?.assaultLocationDescription || '',
                };
            }
            data = {...data, ...dataToBlock};
            setInjury(prevState => ({...prevState, ...data}));
            disableData.current = {...cleanObject(dataToBlock)};
        }
    }, []);

    useEffect(() => {
        setDrugsAlcoholInvolved(props.drugsAlcoholInvolvedValue);
    }, [props.drugsAlcoholInvolvedValue]);

    useEffect(() => {
        if (!props.ecds && props.drugsAlcoholInvolved) {
            props.drugsAlcoholInvolved(drugsAlcoholInvolved);
        }
    }, [drugsAlcoholInvolved]);

    const updateData = () => {
        if (!firstUpdate.current || props.ecds) {
            const data = {
                // Leave for backward compatibility
                InjuryDate: injury.InjuryDateTime ? moment(injury.InjuryDateTime).utc().format(config.ecdsDateFormat) : null,
                InjuryTime: injury.InjuryDateTime ? moment(injury.InjuryDateTime).utc().format(config.ecdsTimeFormat) : null,
                // New combined field
                InjuryDateTime: injury.InjuryDateTime ? moment(injury.InjuryDateTime).utc().format(config.ecdsDateTimeFormat) : null,
                EmergencyCarePlaceOfInjury_SnomedCt: props.noCode ? injury.EmergencyCarePlaceOfInjury_SnomedCt : (injury.EmergencyCarePlaceOfInjury_SnomedCt ? injury.EmergencyCarePlaceOfInjury_SnomedCt.snomedCode : null),
                EmergencyCarePlaceOfInjury_LatitudeAndLongitude: {
                    Latitude: injury.Latitude,
                    Longitude: injury.Longitude,
                },
                EmergencyCareInjuryIntent_SnomedCt: props.noCode ? injury.EmergencyCareInjuryIntent_SnomedCt : (injury.EmergencyCareInjuryIntent_SnomedCt ? injury.EmergencyCareInjuryIntent_SnomedCt.snomedCode : null),
                EmergencyCareInjuryActivityStatus_SnomedCt: props.noCode ? injury.EmergencyCareInjuryActivityStatus_SnomedCt : (injury.EmergencyCareInjuryActivityStatus_SnomedCt ? injury.EmergencyCareInjuryActivityStatus_SnomedCt.snomedCode : null),
                EmergencyCareInjuryActivityType_SnomedCt: props.noCode ? injury.EmergencyCareInjuryActivityType_SnomedCt : (injury.EmergencyCareInjuryActivityType_SnomedCt ? injury.EmergencyCareInjuryActivityType_SnomedCt.snomedCode : null),
                EmergencyCareInjuryMechanism_SnomedCt: props.noCode ? injury.EmergencyCareInjuryMechanism_SnomedCt : (injury.EmergencyCareInjuryMechanism_SnomedCt ? injury.EmergencyCareInjuryMechanism_SnomedCt.snomedCode : null),
                EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt: props.noCode ? injury.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt : getSnomedCode(injury.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt),
                AssaultLocationDescription: injury.AssaultLocationDescription || '',
            };
            props.setData(data);
        }
    };

    useEffect(() => {
        if (validate()) {
            updateData();
        }
    }, [injury]);

    const changeHandler = (event) => {
        firstUpdate.current = false;
        setInjury((prevState) => ({...prevState, [event.target.name]: event.target.value}));
    };

    const setDataDictionary = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    const toggle = () => setToggleCord(!toggleCord);

    const getSnomedCode = (list) => {
        let data = [];
        list.map(entry => data.push(entry.snomedCode));
        return data;
    };

    const multiDataHandler = (_e, vals) => {
        changeHandler({
            target: {
                name: 'EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt',
                value: vals,
            },
        });
    };

    const validate = () => {
        let check = true;

        const touch = !!injury.InjuryDateTime || !!injury.EmergencyCarePlaceOfInjury_SnomedCt || !!injury.Latitude
            || !!injury.Longitude || !!injury.EmergencyCareInjuryIntent_SnomedCt || !!injury.EmergencyCareInjuryActivityStatus_SnomedCt
            || !!injury.EmergencyCareInjuryActivityType_SnomedCt || !!injury.EmergencyCareInjuryMechanism_SnomedCt
            || !_.isEmpty(injury.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt);

        let errorData = {
            dateTime: props.ecds ? (touch ? !injury.InjuryDateTime : false) : (props.selectedForm ? false : !injury.InjuryDateTime),
            place: injury.EmergencyCarePlaceOfInjury_SnomedCt ? !props.checkLenght(6, 18, injury.EmergencyCarePlaceOfInjury_SnomedCt) : false,
            latitude: injury.Latitude !== '' ? !Validator.validLatitude(injury.Latitude) : false,
            longitude: injury.Longitude !== '' ? !Validator.validLongitude(injury.Longitude) : false,
            intent: injury.EmergencyCareInjuryIntent_SnomedCt ? !props.checkLenght(6, 18, injury.EmergencyCareInjuryIntent_SnomedCt) : false,
            status: injury.EmergencyCareInjuryActivityStatus_SnomedCt ? !props.checkLenght(6, 18, injury.EmergencyCareInjuryActivityStatus_SnomedCt) : false,
            type: injury.EmergencyCareInjuryActivityType_SnomedCt ? !props.checkLenght(6, 18, injury.EmergencyCareInjuryActivityType_SnomedCt) : false,
            mechanism: injury.EmergencyCareInjuryMechanism_SnomedCt ? !props.checkLenght(6, 18, injury.EmergencyCareInjuryMechanism_SnomedCt) : false,
        };
        if (!_.isEmpty(injury.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt)) {
            let check = injury.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt?.some(item => !props.checkLenght(6, 18, item));
            errorData = {
                ...errorData,
                alcoholAndDrug: check,
            };
        }
        setError(prevState => ({...prevState, ...errorData}));

        for (let prop in errorData) {
            if (errorData[prop]) {
                check = false;
                break;
            }
        }

        if (props.valid) {
            const validObj = {
                name: 'Injury Characteristics',
                groupStatus: 'R',
                touch: touch,
                mandatory: !injury.InjuryDateTime,
                errors: !check,
                pickers: errorPicker.current !== null,
            };
            props.valid(prevState => ({...prevState, 'injury': validObj}));
        }
        if (props.errorPicker) {
            props.errorPicker(errorPicker.current, 'injuryTimestamp');
        }
        return check;
    };

    const dateHandler = (event) => {
        if (event && moment(event).isValid()) {
            setInjury((prevState) => ({...prevState, InjuryDateTime: event}));
            errorPicker.current = null;
        } else {
            setInjury((prevState) => ({...prevState, InjuryDateTime: null}));
            errorPicker.current = 'Invalid';
        }
    };

    return (
        <div className={classes.root}>
            <Grid container spacing={2} direction="row" style={{marginTop: 1}}>
                <Grid item xs={12}>
                    <AgyleDatePicker
                        name="InjuryTimestamp"
                        label={t('Injury Date and Time')}
                        type="dateTime"
                        value={injury.InjuryDateTime}
                        onChange={dateHandler}
                        minDate={props.patient?.patient?.dob ? moment(props.patient.patient.dob) : null}
                        maxTime={config.injuryDateAfterAdmit ? moment() : calculateArrivalDate(props.patient)}
                        onError={event => errorPicker.current = event}
                        error={error.dateTime || errorPicker.current}
                        readOnly={props.ecds ? !!disableData?.current.InjuryTime : false}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <PlaceOfInjury
                        name="EmergencyCarePlaceOfInjury_SnomedCt"
                        label={t('Place of Injury')}
                        value={injury.EmergencyCarePlaceOfInjury_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCarePlaceOfInjury_SnomedCt', val])}
                        error={error.place}
                        disabled={props.ecds ? !!disableData?.current.EmergencyCarePlaceOfInjury_SnomedCt : false}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                {!props.noCode && (
                    <Grid item xs={12}>
                        <AgyleButton onClick={toggle}>
                            {t('Toggle Geographic Coordinates')}
                        </AgyleButton>

                        {toggleCord &&
                            <Grid container spacing={2} direction="row" style={{marginTop: 1}}>
                                <Grid item xs={12} md={6}>
                                    <AgyleTextField
                                        name="Latitude"
                                        label={t('Latitude')}
                                        onChange={changeHandler}
                                        value={injury.Latitude}
                                        error={error.latitude}
                                        helperText={error.latitude ? 'Input must follow an regex 00.000000' : ''}
                                        readOnly={!!disableData?.current.Latitude}
                                        fullWidth
                                        debounce
                                        noBorder={false}
                                        withNewBackground
                                        size="large"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <AgyleTextField
                                        name="Longitude"
                                        label={t('Longitude')}
                                        onChange={changeHandler}
                                        value={injury.Longitude}
                                        error={error.longitude}
                                        helperText={error.longitude ? 'Input must follow an regex 000.000000' : ''}
                                        readOnly={!!disableData?.current.Longitude}
                                        fullWidth
                                        debounce
                                        noBorder={false}
                                        withNewBackground
                                        size="large"
                                    />
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                )}

                <Grid item xs={12}>
                    <InjuryIntent
                        name="EmergencyCareInjuryIntent_SnomedCt"
                        label={t('Injury Intent')}
                        value={injury.EmergencyCareInjuryIntent_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareInjuryIntent_SnomedCt', val])}
                        error={error.intent}
                        disabled={props.ecds ? !!disableData?.current.EmergencyCareInjuryIntent_SnomedCt : false}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <InjuryActivityStatus
                        name="EmergencyCareInjuryActivityStatus_SnomedCt"
                        label={t('Injury Activity Status')}
                        value={injury.EmergencyCareInjuryActivityStatus_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareInjuryActivityStatus_SnomedCt', val])}
                        error={error.status}
                        disabled={props.ecds ? !!disableData?.current.EmergencyCareInjuryActivityStatus_SnomedCt : false}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <InjuryActivityType
                        name="EmergencyCareInjuryActivityType_SnomedCt"
                        label={t('Injury Activity Type')}
                        value={injury.EmergencyCareInjuryActivityType_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareInjuryActivityType_SnomedCt', val])}
                        error={error.type}
                        disabled={props.ecds ? !!disableData?.current.EmergencyCareInjuryActivityType_SnomedCt : false}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <InjuryMechanism
                        name="EmergencyCareInjuryMechanism_SnomedCt"
                        label={t('Injury Mechanism')}
                        value={injury.EmergencyCareInjuryMechanism_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareInjuryMechanism_SnomedCt', val])}
                        error={error.mechanism}
                        disabled={props.ecds ? !!disableData?.current.EmergencyCareInjuryMechanism_SnomedCt : false}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                {!props.ecds &&
                    <Grid item xs={12} className={classes.divRow}>
                        <AgyleBlockSwitch
                            value={drugsAlcoholInvolved}
                            onChange={() => setDrugsAlcoholInvolved(prevState => !prevState)}
                        />
                        <FormLabel sx={{ml: 1}}>
                            {t('Were drugs or alcohol involved?')}
                        </FormLabel>
                    </Grid>
                }

                {(props.ecds || drugsAlcoholInvolved) &&
                    <Grid item xs={12}>
                        <InjuryDrugAlcohol
                            name="EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt"
                            label={t('Injury Alcohol or Drug Involvement')}
                            value={injury.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt}
                            onChange={multiDataHandler}
                            error={error.alcoholAndDrug}
                            disabled={props.ecds ? !!disableData?.current.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt : false}
                            admitDate={props.patient?.admission?.startDate}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>
                }

                <Grid item xs={12}>
                    <AgyleTextField
                        name="AssaultLocationDescription"
                        label={t('Assault Location Description')}
                        onChange={changeHandler}
                        value={injury.AssaultLocationDescription || ''}
                        error={error.assault}
                        readOnly={!!disableData?.current.assault}
                        fullWidth
                        debounce
                        disabled={props.ecds ? !!disableData?.current.AssaultLocationDescription : false}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default Injury;
